import { projectApi } from '../services/projectApi';
import { API_BASE } from '../config';

export function isBackendAssetUrl(url: string): boolean {
    return url.startsWith('/assets/') || url.startsWith(`${API_BASE}/assets/`);
}

export function normalizeAssetUrl(url: string): string {
    let normalized = url;
    if (normalized.startsWith(`${API_BASE}/assets/`)) {
        normalized = normalized.replace(API_BASE, '');
    }
    if (normalized.startsWith('/assets/')) {
        normalized = normalized.replace(/\\/g, '/');
    }
    return normalized;
}

interface SaveAssetOptions {
    nodeId: string;
    prefix: string;
    metadata?: Record<string, string>;
    isVideo?: boolean;
}

export async function saveAssets(
    projectId: string | null,
    urls: string[],
    options: SaveAssetOptions
): Promise<{ savedUrls: string[]; hasFailure: boolean }> {
    if (!projectId || urls.length === 0) {
        return { savedUrls: urls, hasFailure: false };
    }

    let hasFailure = false;

    const savedUrls = await Promise.all(
        urls.map(async (url, idx) => {
            if (isBackendAssetUrl(url)) return url;

            try {
                const filename = `${options.prefix}_${options.nodeId}_${idx + 1}`;
                const asset = await projectApi.downloadAndSaveAsset(
                    projectId,
                    url,
                    filename,
                    options.metadata || {}
                );
                return normalizeAssetUrl(asset.url);
            } catch (err: any) {
                console.error(`[生成] 保存${options.isVideo ? '视频' : '图片'}失败:`, err.message, 'URL:', url);
                hasFailure = true;
                return url;
            }
        })
    );

    return { savedUrls, hasFailure };
}

export function createMediaUpdate(
    savedUrls: string[],
    isVideo: boolean
): { image?: string; images?: string[]; videoUri?: string; videoUris?: string[] } {
    if (isVideo) {
        return {
            videoUri: savedUrls[0],
            videoUris: savedUrls
        };
    }
    return {
        image: savedUrls[0],
        images: savedUrls
    };
}

export async function saveSingleAsset(
    projectId: string | null,
    url: string,
    filename: string,
    metadata: Record<string, string>,
    isVideo: boolean = false
): Promise<string> {
    if (!projectId || isBackendAssetUrl(url)) return url;

    try {
        const asset = await projectApi.downloadAndSaveAsset(projectId, url, filename, metadata);
        return normalizeAssetUrl(asset.url);
    } catch (err: any) {
        console.error(`[生成] 保存${isVideo ? '视频' : '图片'}失败:`, err.message, 'URL:', url);
        return url;
    }
}

export async function fetchBlobAsDataUrl(url: string): Promise<string> {
    const absUrl = url.startsWith('/') ? `${API_BASE}${url}` : url;
    const resp = await fetch(absUrl);
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const blob = await resp.blob();
    return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('FileReader failed'));
        reader.readAsDataURL(blob);
    });
}

export async function fetchImageForCanvas(url: string): Promise<HTMLImageElement> {
    const normalizedUrl = normalizeAssetUrl(url);

    if (normalizedUrl.startsWith('data:')) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = (e) => reject(new Error('Image load failed: ' + String(e)));
            img.src = normalizedUrl;
        });
    }

    const proxyUrl = normalizedUrl.replace(new RegExp(`^${API_BASE.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`), '');
    const response = await fetch(proxyUrl);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);

    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(objectUrl);
            resolve(img);
        };
        img.onerror = (e) => {
            URL.revokeObjectURL(objectUrl);
            reject(new Error('Image load failed: ' + String(e)));
        };
        img.src = objectUrl;
    });
}
